"use client";
import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Search, Calendar, User } from "lucide-react";
import { useAuth } from "./context/AuthContext";

const tabs = [
  { key: "home", to: "/", label: "Home", icon: Home, isActive: (p) => p === "/" },
  {
    key: "search",
    to: "/therapists",
    label: "Search",
    icon: Search,
    isActive: (p) =>
      p.startsWith("/therapists") ||
      p.startsWith("/find-therapist") ||
      p.startsWith("/massage-therapists") ||
      p.startsWith("/services"),
  },
  {
    key: "book",
    to: "/book",
    label: "Book",
    icon: Calendar,
    isActive: (p) => p.startsWith("/book"),
    primary: true,
  },
  {
    key: "account",
    to: "/dashboard",
    label: "Account",
    icon: User,
    isActive: (p) =>
      p.startsWith("/dashboard") ||
      p.startsWith("/rewards") ||
      p.startsWith("/referrals") ||
      p.startsWith("/auth"),
  },
];

// Routes where the bottom nav would conflict with their own sticky footer
// or would be visually noisy (booking funnel, auth, post-appointment).
const HIDDEN_PATH_PREFIXES = [
  "/book",
  "/auth",
  "/post-appointment",
  "/chatbot-training",
  "/admin",
  "/gift-cards/customize",
  "/gift-cards/payment",
];

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Hide on funnel/auth pages so we don't double-stack sticky bars.
  const shouldHide = HIDDEN_PATH_PREFIXES.some((p) =>
    location.pathname.startsWith(p),
  );

  // Toggle a body class so the global mobile bottom-padding rule turns off
  // on routes where the nav is hidden — keeps funnel pages flush with the viewport.
  useEffect(() => {
    if (shouldHide) {
      document.body.classList.add("no-mobile-bottom-nav");
    } else {
      document.body.classList.remove("no-mobile-bottom-nav");
    }
    return () => document.body.classList.remove("no-mobile-bottom-nav");
  }, [shouldHide]);

  if (shouldHide) return null;

  const handleAccountClick = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      navigate("/auth", { state: { returnTo: "/dashboard" } });
    }
  };

  return (
    <nav
      role="navigation"
      aria-label="Primary"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] safe-bottom"
    >
      <ul className="grid grid-cols-4 gap-1 px-2 pt-1.5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.isActive(location.pathname);
          const onClick = tab.key === "account" ? handleAccountClick : undefined;

          if (tab.primary) {
            return (
              <li key={tab.key} className="flex items-start justify-center">
                <Link
                  to={tab.to}
                  className="flex flex-col items-center justify-center gap-0.5 -mt-5 rounded-full bg-gradient-to-br from-[#98C454] to-[#7aa840] text-white w-14 h-14 shadow-[0_8px_24px_rgba(152,196,84,0.4)] active:scale-95 transition-transform"
                  aria-label={tab.label}
                >
                  <Icon className="h-6 w-6" />
                </Link>
              </li>
            );
          }

          return (
            <li key={tab.key}>
              <Link
                to={tab.to}
                onClick={onClick}
                aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl transition-colors ${
                  active
                    ? "text-[#7aa840]"
                    : "text-gray-500 hover:text-gray-700 active:bg-gray-100"
                }`}
              >
                <Icon
                  className={`h-5 w-5 ${active ? "stroke-[2.5]" : ""}`}
                  aria-hidden="true"
                />
                <span className="text-[11px] font-medium leading-none">
                  {tab.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
